import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Session, User } from '@supabase/supabase-js';
import { supabase, toAppError } from '@/services/supabase';

export interface Profile {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  locale: string;
  timezone: string;
  /**
   * Preferências soltas do usuário (jsonb).
   *
   * Chaves conhecidas ficam declaradas; o índice aberto existe para que
   * uma tela nova possa gravar a sua sem migração e sem quebrar as
   * outras — a RPC que grava faz merge, nunca sobrescreve.
   */
  preferences: { early_access?: boolean } & Record<string, unknown>;
  default_workspace_id: string | null;
  onboarded_at: string | null;
  /**
   * Tema escolhido pela pessoa.
   *
   * A chave estrangeira no banco garante que só existe código de tema
   * real aqui — a tela injeta a cor correspondente no CSS, e string livre
   * neste campo seria injeção no `style` do documento.
   */
  theme_code: string;
}

/** O que o PostgREST devolve: `preferences` é jsonb, logo anulável. */
type RawProfile = Omit<Profile, 'preferences' | 'theme_code'> & { theme_code: string | null } & {
  preferences: unknown;
};

/**
 * Sessão e perfil do usuário.
 *
 * `initialized` existe para os guards de rota: sem ele, o primeiro
 * carregamento redireciona para /login antes de a sessão ser restaurada
 * do storage, e o usuário logado vê a tela de login piscar.
 */
export const useAuthStore = defineStore('auth', () => {
  const session = ref<Session | null>(null);
  const profile = ref<Profile | null>(null);

  /**
   * `preferences` é jsonb e pode chegar nulo. Normalizar na fronteira
   * poupa `?? {}` em cada leitura — e um `undefined` vazando daqui viraria
   * um toggle que nunca liga.
   */
  function normalize(row: RawProfile | null): Profile | null {
    if (!row) return null;
    return {
      ...row,
      preferences: (row.preferences ?? {}) as Profile['preferences'],
      // Conta antiga pode não ter tema gravado. O padrão evita a tela
      // ficar sem cor de destaque nenhuma.
      theme_code: row.theme_code ?? 'default',
    };
  }
  const initialized = ref(false);
  const loading = ref(false);

  const user = computed<User | null>(() => session.value?.user ?? null);
  const isAuthenticated = computed(() => session.value !== null);
  const hasOnboarded = computed(() => profile.value?.onboarded_at !== null);

  async function loadProfile(): Promise<void> {
    if (!user.value) {
      profile.value = null;
      return;
    }

    const { data, error } = await supabase
      .from('profiles')
      .select(
        'id, full_name, avatar_url, locale, timezone, preferences, default_workspace_id, onboarded_at, theme_code',
      )
      .eq('id', user.value.id)
      .maybeSingle();

    if (error) throw toAppError(error, 'Não foi possível carregar seu perfil.');
    profile.value = normalize(data);
  }

  /** Restaura a sessão e passa a ouvir mudanças. Chamado uma vez, no boot. */
  async function initialize(): Promise<void> {
    if (initialized.value) return;

    const { data } = await supabase.auth.getSession();
    session.value = data.session;

    if (session.value) {
      try {
        await loadProfile();
      } catch {
        // Perfil indisponível não pode impedir o boot: os guards decidem
        // o que fazer, e a tela mostra o erro no contexto certo.
      }
    }

    supabase.auth.onAuthStateChange((event, newSession) => {
      session.value = newSession;
      if (event === 'SIGNED_OUT') {
        profile.value = null;
      } else if (newSession && !profile.value) {
        void loadProfile();
      }
    });

    initialized.value = true;
  }

  async function signIn(email: string, password: string): Promise<void> {
    loading.value = true;
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });
      if (error) throw toAppError(error);
      await loadProfile();
    } finally {
      loading.value = false;
    }
  }

  async function signUp(email: string, password: string, fullName: string): Promise<void> {
    loading.value = true;
    try {
      const { error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: { full_name: fullName.trim() },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw toAppError(error);
      // profiles é criado pelo trigger on_auth_user_created — nunca aqui.
      await loadProfile();
    } finally {
      loading.value = false;
    }
  }

  async function signOut(): Promise<void> {
    await supabase.auth.signOut();
    session.value = null;
    profile.value = null;
  }

  async function requestPasswordReset(email: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw toAppError(error);
  }

  async function updatePassword(newPassword: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw toAppError(error);
  }

  /**
   * `preferences` fica de fora do patch de propósito: ela é gravada pela
   * RPC `save_user_preferences`, que faz MERGE. Um update direto aqui
   * apagaria as chaves que outra tela acabou de gravar.
   */
  async function updateProfile(patch: Partial<Omit<Profile, 'id' | 'preferences'>>): Promise<void> {
    if (!user.value) throw toAppError(new Error('not_authenticated'));

    const { data, error } = await supabase
      .from('profiles')
      .update(patch)
      .eq('id', user.value.id)
      .select(
        'id, full_name, avatar_url, locale, timezone, preferences, default_workspace_id, onboarded_at, theme_code',
      )
      .single();

    if (error) throw toAppError(error, 'Não foi possível salvar seu perfil.');
    profile.value = normalize(data);
  }

  return {
    session,
    profile,
    user,
    initialized,
    loading,
    isAuthenticated,
    hasOnboarded,
    initialize,
    loadProfile,
    signIn,
    signUp,
    signOut,
    requestPasswordReset,
    updatePassword,
    updateProfile,
  };
});
